import {forceLogout, getAccessToken} from "./authority";
import {toast} from "react-toastify";

// Get language from localStorage, default to 'vi'
const getLang = () => localStorage.getItem('sas_school_language') || 'vi';
let lang = getLang();


let token = getAccessToken();

// Hết phiên (HTTP 401 — contract ba-shared/api-contracts/auth/session-expiry.md):
// báo user lý do rồi mới về trang đăng nhập (chuyển trang ngay sẽ huỷ toast).
// Trả về true nếu là 401 để caller set isExpired và dừng xử lý.
// Chống trùng 2 lớp: cờ này + cờ isLoggingOut trong forceLogout.
let sessionExpiredNotified = false;
const handleUnauthorized = (response) => {
    if (response?.status !== 401) return false;
    if (!sessionExpiredNotified) {
        sessionExpiredNotified = true;
        toast.warning("Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại!");
        setTimeout(forceLogout, 1500);
    }
    return true;
};

/**
 * Mô tả quyền còn thiếu thành câu đọc được, lấy từ `BaseResponse.requiredPermissions`
 * (SCR-071 — server tự khai guard đòi quyền gì, thay vì để vận hành dò ngược trong code).
 *
 * Server đã ghép sẵn câu này vào `message[0]`; hàm ở đây là lớp dự phòng cho hai trường hợp
 * thật sự xảy ra: (a) jar chưa deploy bản mới nên `message` vẫn là `["FORBIDDEN"]`,
 * (b) 403 đi qua nhánh khác làm mất `message` nhưng vẫn còn danh sách quyền.
 *
 * @param {Object} res response gốc {success, errorCode, requiredPermissions...}
 * @returns {String} "" nếu server không khai quyền nào
 */
export const requiredPermissionText = (res) => {
    const list = res && res.requiredPermissions;
    if (!Array.isArray(list) || list.length === 0) return "";
    const parts = list.map((p) => {
        const resource = (p && p.resource) || "?";
        const action = (p && p.action) || "?";
        const code = p && p.code ? ` (${p.code})` : " (chưa có trong danh mục quyền)";
        const name = p && p.name ? ` — ${p.name}` : "";
        return `${resource}|${action}${code}${name}`;
    });
    if (parts.length === 1) return `Thiếu quyền ${parts[0]}`;
    return `Thiếu quyền, cần MỘT trong: ${parts.join(" hoặc ")}`;
};

/**
 * Mã lỗi nghiệp vụ → câu tiếng Việt hiển cho người dùng.
 *
 * ⚠️ **Vì sao phải dịch lại ở client dù server đã có `messages_vi.properties`:**
 * `MessageSourceUtils` đọc `LocaleContextHolder.getLocale()`, tức **`Accept-Language` của
 * trình duyệt** — cùng một lỗi có thể ra tiếng Anh (bundle mặc định) hay tiếng Việt tuỳ máy.
 * Thêm nữa `qlts` bật `spring.messages.use-code-as-default-message`, nên khoá thiếu trong
 * bundle sẽ trả về **chính mã lỗi trần** (400 thay vì 500) — chịu được 400, nhưng không
 * được để user đọc "CUSTOMER_STATE_CONFLICT".
 *
 * Chỉ để ở đây những mã **không có tham số** (câu cố định). Mã có `%s` thì phải dùng câu
 * server ghép sẵn, đừng thêm vào đây.
 *
 * SCR-083 Đợt 3 — 5 luật tập trạng thái (contract `crm/customer-multi-state-source.md` §5).
 */
export const ERROR_CODE_MESSAGES = {
    CUSTOMER_STATE_EMPTY: "Khách hàng phải có ít nhất 1 trạng thái.",
    CUSTOMER_STATE_RAW_ALONE: "“Data thô” phải đứng một mình, không đi kèm trạng thái khác.",
    CUSTOMER_STATE_RAW_ONE_WAY: "Khách hàng đã vào phễu, không thể chuyển ngược về “Data thô”.",
    CUSTOMER_STATE_CONFLICT: "Không thể vừa kết thúc sớm (Từ chối / Mất liên lạc) vừa đang theo đuổi "
        + "(Tiềm năng / Hẹn gặp / Đã test). Bỏ bớt một nhóm rồi lưu lại.",
    CUSTOMER_STATE_NEED_REGISTERED: "Trạng thái “Không nhập học” chỉ dùng cho khách đã ghi danh.",
};

/**
 * Lấy câu lỗi HIỂN THỊ ĐƯỢC từ `BaseResponse` của server.
 *
 * ⚠️ Bẫy: `BaseResponse.message` phía server là **List<String>**
 * (api-common `BaseResponse.java` + `GlobalExceptionHandle`), nên
 * `toast.error(res.message)` truyền vào react-toastify một MẢNG. react-toastify v9 lọc nội dung
 * bằng `canBeRendered()` — chỉ nhận string/number/ReactElement/function — và **bỏ qua toast
 * trong im lặng** khi nhận mảng ⇒ user bấm nút, thao tác fail mà KHÔNG thấy thông báo nào.
 * Vì vậy mọi chỗ báo lỗi phải đi qua hàm này thay vì `res.message` trần.
 *
 * Ngoài ra đổi vài mã kỹ thuật do handler chung trả về thành câu tiếng Việt:
 *  - 500 → message = ["ERROR"]      (GlobalExceptionHandle.handleExceptions)
 *  - 403 → message = ["FORBIDDEN"]  (handleAccessDeniedException)
 *
 * @param {Object} res      response gốc {success, message, errorCode...}
 * @param {String} fallback câu thay thế khi server không nói gì
 * @returns {String} luôn là chuỗi — an toàn để đưa vào toast / alert
 */
export const apiMessage = (res, fallback = "Có lỗi xảy ra, vui lòng thử lại!") => {
    // Mã lỗi nghiệp vụ có câu tiếng Việt riêng thì ưu tiên câu đó: câu server gửi lên có thể là
    // tiếng Anh (locale theo Accept-Language) hoặc chính mã lỗi (use-code-as-default-message).
    const byCode = res && ERROR_CODE_MESSAGES[res.errorCode];
    if (byCode) return byCode;
    const raw = res && res.message;
    const text = Array.isArray(raw)
        ? raw.filter(Boolean).map(String).join("\n")
        : (typeof raw === "string" ? raw : "");
    // SCR-071: 403 mà server khai được quyền thiếu thì nói thẳng mã quyền, đừng che bằng câu chung.
    const missing = requiredPermissionText(res);
    if (!text.trim()) return missing || fallback;
    if (text === "ERROR") return "Hệ thống gặp lỗi khi xử lý yêu cầu, vui lòng thử lại hoặc báo quản trị.";
    if (text === "FORBIDDEN") return missing || "Bạn không có quyền thực hiện thao tác này.";
    return text;
};

export const request = (request, callback) =>{
    token = getAccessToken();
    lang = getLang(); // Update language on each request
    if (token === undefined)
        token = getAccessToken();
    const abortController = request.timeoutMs ? new AbortController() : null;
    const timeoutId = abortController
        ? setTimeout(() => abortController.abort(), request.timeoutMs)
        : null;
    let config = {
        method: request.method,
        headers: token ?  {
            "Authorization": "Bearer " + token,
            "Content-Type": "application/json; charset=UTF-8",
            "Accept-Language": lang
        } : {
            "Content-Type": "application/json; charset=UTF-8",
            "Accept-Language": lang
        },
        body: JSON.stringify(request.body),
        signal: abortController ? abortController.signal : undefined
        // body: request.body
    }
    let isExpired = false;
    return fetch(request?.url,config)
        .then(async response => {
            if (handleUnauthorized(response)) {
                isExpired = true;
                return null;
            }
            // else if (response?.status == 500) {
            //     return {"success":true,"data": null,"errorCode":null,"message":null,"refCode":null}
            // }
            // console.log("check response fetch>>>>", response)
            return response.json()
        })
        .then((res) => {
            if (!isExpired) callback(res);
        })
        .catch((error) => {
            console.error('API Error:', error);
            if (isExpired) return;
            if (error?.name === 'AbortError') {
                if (callback) {
                    callback({
                        success: false,
                        errorCode: 'REQUEST_TIMEOUT',
                        message: 'Yêu cầu quá thời gian chờ, vui lòng thử lại'
                    });
                }
                return;
            }
            if (callback) {
                callback({ success: false, errorCode: 'NETWORK_ERROR', message: error?.message || 'Lỗi kết nối mạng' });
            }
        })
        .finally(() => {
            if (timeoutId) clearTimeout(timeoutId);
        })
};

export const requestLogin = (request, callback) =>{
    lang = getLang(); // Update language on each request
    let config = {
        method: request.method,
        headers: {
            "Content-Type": "application/json; charset=UTF-8",
            "Accept-Language": lang
        },
        body: JSON.stringify(request.body)
        // body: request.body
    }
    return fetch(request?.url,config)
        .then(async response => {
            if (response?.status === 401) {
                toast.error("Sai tên đăng nhập hoặc mật khẩu!")
            }
            return response.json()
        })
        .then((res) => {
            callback(res);
        })
        .catch((error) => {
            console.log(error)
            toast.error("Sai tên đăng nhập hoặc mật khẩu!")
            // callback(error.json())
        })
};

export const requestUploadPublic = (request, callback) =>{
    lang = getLang(); // Update language on each request
    let config = {
        method: request.method,
        headers: {
            "Accept-Language": lang
        },
        // body: JSON.stringify(request.body)
        body: request.body
    }
    let isExpired = false;
    return fetch(request?.url,config)
        .then(async response => {
            if (handleUnauthorized(response)) {
                isExpired = true;
                return null;
            }
            return response.json()
        })
        .then((res) => {
            if (!isExpired) callback(res);
        })
        .catch((error) => {
            if (isExpired) return;
            console.error('Upload Public Error:', error);
            if (callback) {
                callback({ success: false, errorCode: 'NETWORK_ERROR', message: error?.message || 'Lỗi kết nối mạng' });
            }
        });
};

export const requestUpload = (request, callback) =>{
    token = getAccessToken();
    lang = getLang(); // Update language on each request
    let config = {
        method: request.method,
        headers: {
            "Authorization": "Bearer " + token,
            "Accept-Language": lang
        },
        body: request.body,
    }
    let isExpired = false;
    return fetch(request?.url,config)
        .then(async response => {
            if (handleUnauthorized(response)) {
                isExpired = true;
                return null;
            }
            return response.json()
        })
        .then((res) => {
            if (!isExpired) callback(res);
        })
        .catch((error) => {
            if (isExpired) return;
            console.error('Upload Error:', error);
            if (callback) {
                callback({ success: false, errorCode: 'NETWORK_ERROR', message: error?.message || 'Lỗi kết nối mạng' });
            }
        });
};

/**
 * Câu lỗi đọc được cho người dùng từ `BaseResponse` lỗi của server.
 *
 * Server trả `message` dạng **mảng mã lỗi** (`["FORBIDDEN"]`) chứ không phải câu tiếng Việt, nên
 * dịch các mã hay gặp; mã lạ thì trả nguyên văn còn hơn nuốt mất.
 *
 * SCR-071: câu 403 đóng hộp cũ ("cấp quyền 'Tải' (DOWNLOAD)") đoán sai khá thường — nhiều endpoint
 * export gác `VIEW`/`EXPORT` chứ không phải `DOWNLOAD`. Nên khi server đã khai quyền thiếu
 * (`requiredPermissions`) hoặc đã gửi câu thật thì dùng cái đó; câu đoán chỉ còn là chốt cuối.
 */
const downloadErrorMessage = (body, status) => {
    const raw = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
    const code = body?.errorCode || raw;
    if (code === 'FORBIDDEN' || status === 403) {
        const missing = requiredPermissionText(body);
        if (missing) return `${missing}. Đề nghị quản trị cấp quyền này rồi tải lại.`;
        if (raw && raw !== 'FORBIDDEN') return raw;
        return "Bạn không có quyền tải file này. Đề nghị quản trị cấp quyền 'Tải' (DOWNLOAD) của chức năng tương ứng.";
    }
    if (status === 404) {
        return "Không tìm thấy dữ liệu để xuất file, vui lòng kiểm tra lại!";
    }
    return raw || "Download không thành công, vui lòng thử lại!";
};

/**
 * Tải file nhị phân từ một endpoint GET/POST.
 *
 * ⚠️ Endpoint export trả **file khi thành công** nhưng **JSON `BaseResponse` khi lỗi**
 * (403 thiếu quyền, 400 sai tham số, 404 không có dữ liệu). Vì vậy phải xem `response.ok` và
 * `Content-Type` TRƯỚC khi gọi `blob()` — bản cũ lưu thẳng mọi status ngoài 400/404/500 thành
 * file, nên 403 rơi vào ổ cứng dưới dạng .xlsx 92 byte chứa `{"errorCode":"FORBIDDEN"}` và Excel
 * báo file hỏng. Người dùng mất cả buổi tưởng lỗi máy chủ, trong khi chỉ thiếu một ô tích quyền.
 *
 * Tên file lấy theo thứ tự: header `Content-Disposition` → `request.fileName`.
 *
 * @param {Object} request - { url, method, body, fileName }
 * @param {Function} callback - nhận { success, message }
 */
export const requestDownload = (request, callback) =>{
    token = getAccessToken();
    lang = getLang(); // Update language on each request
    let config = {
        method: request.method,
        headers: {
            "Authorization": "Bearer " + token,
            // "Content-Type": "multipart/form-data",
            "Accept-Language": lang
        },
        // body: JSON.stringify(request.body)
        body: request.body
    }
    let isExpired = false;
    return fetch(request?.url,config)
        .then(async response => {
            if (handleUnauthorized(response)) {
                isExpired = true;
                return null;
            }
            if (!response.ok) {
                const body = await response.json().catch(() => null);
                throw new Error(downloadErrorMessage(body, response.status));
            }
            // Gateway/Spring có lúc trả 200 kèm JSON lỗi — vẫn không được lưu thành file.
            const contentType = response.headers.get('Content-Type') || '';
            if (contentType.includes('json')) {
                const body = await response.json().catch(() => null);
                throw new Error(downloadErrorMessage(body, response.status));
            }
            const disposition = response.headers.get('Content-Disposition') || '';
            const matched = /filename[^;=\n]*=(?:UTF-8'')?"?([^";\n]*)"?/i.exec(disposition);
            const fileName = matched ? decodeURIComponent(matched[1].trim()) : request.fileName;
            return { blob: await response.blob(), fileName };
        })
        .then((res) => {
            if (isExpired || !res) return;
            const fileURL = URL.createObjectURL(res.blob);
            let a = document.createElement('a');
            a.href = fileURL;
            a.download = res.fileName || request.fileName || 'download';
            a.click();
            // Revoke objectURL to prevent memory leak
            setTimeout(() => URL.revokeObjectURL(fileURL), 100);
            if (callback) callback({ success: true });
        })
        .catch(error => {
            if (isExpired) return;
            console.error('Download Error:', error);
            const message = error?.message || "Download không thành công, vui lòng thử lại!";
            toast.error(message);
            // Phải gọi callback cả khi lỗi, nếu không nút gọi hàm này kẹt mãi ở "Đang xuất...".
            if (callback) callback({ success: false, message });
        })
};

/**
 * Tải file từ endpoint nhận **JSON body** (`@RequestBody`), khác `requestDownload` ở hai chỗ
 * bắt buộc: có `Content-Type: application/json` và `JSON.stringify(body)`.
 * Thiếu một trong hai thì Spring trả 415/400 vì `fetch` gửi object thành chuỗi "[object Object]".
 *
 * Tên file lấy theo thứ tự: header `Content-Disposition` của server → `request.fileName`.
 * Server đặt tên có timestamp nên ưu tiên header để 2 lần tải không đè lên nhau.
 *
 * ⚠️ Endpoint export trả **file nhị phân khi thành công** nhưng vẫn trả **JSON `BaseResponse`
 * khi lỗi** (400 thiếu tham số, 403 thiếu quyền). Vì vậy phải xem status TRƯỚC khi gọi `blob()`,
 * nếu không sẽ lưu ra file .xlsx chứa JSON và Excel báo hỏng file.
 *
 * @param {Object} request - { url, method='POST', body, fileName }
 * @param {Function} callback - nhận { success, message }
 */
export const requestDownloadJson = (request, callback) => {
    token = getAccessToken();
    lang = getLang();
    const config = {
        method: request.method || 'POST',
        headers: {
            "Authorization": "Bearer " + token,
            "Content-Type": "application/json; charset=UTF-8",
            "Accept-Language": lang
        },
        body: JSON.stringify(request.body)
    };
    return fetch(request?.url, config)
        .then(async response => {
            if (handleUnauthorized(response)) return null;
            if (!response.ok) {
                // Lỗi luôn là JSON — đọc lấy đúng câu server nói thay vì báo chung chung
                const err = await response.json().catch(() => null);
                const message = Array.isArray(err?.message) ? err.message.join(', ') : err?.message;
                throw new Error(message || "Xuất file không thành công, vui lòng thử lại!");
            }
            const disposition = response.headers.get('Content-Disposition') || '';
            const matched = /filename[^;=\n]*=(?:UTF-8'')?"?([^";\n]*)"?/i.exec(disposition);
            const fileName = matched ? decodeURIComponent(matched[1].trim()) : request.fileName;
            return {blob: await response.blob(), fileName};
        })
        .then((res) => {
            if (!res) return;
            const fileURL = URL.createObjectURL(res.blob);
            const a = document.createElement('a');
            a.href = fileURL;
            a.download = res.fileName || 'download.xlsx';
            a.click();
            setTimeout(() => URL.revokeObjectURL(fileURL), 100);
            if (callback) callback({success: true});
        })
        .catch(error => {
            console.error('Download Error:', error);
            const message = error?.message || "Xuất file không thành công, vui lòng thử lại!";
            toast.error(message);
            if (callback) callback({success: false, message});
        });
};

export const requestShow = (request, callback) =>{
    token = getAccessToken();
    lang = getLang(); // Update language on each request
    let config = {
        method: request.method,
        headers: {
            "Authorization": "Bearer " + token,
            // "Content-Type": "multipart/form-data",
            "Accept-Language": lang
        },
        // body: JSON.stringify(request.body)
        body: request.body
    }
    let isExpired = false;
    return fetch(request?.url,config)
        .then(async response => {
            if (handleUnauthorized(response)) {
                isExpired = true;
                return null;
            }
            else if (response?.status === 500) {
                return response.json()
            }
            else if (response?.status === 400) {
                return response.json()
            }
            else if (response?.status === 404) {
                toast.error("File không tồn tại hoặc đã bị mất, vui lòng thử lại!")
                return response.json()
            }
            else return response.blob()
        })
        .then((res) => {
            if (isExpired) return;
            // if (!res.success) {
            //     return toast.error("Download không thành công, vui lòng thử lại!")
            // }  else {
            if (res) {
                const fileURL = URL.createObjectURL(res);
                callback(fileURL);
            }
            // }
            // const fileURL = URL.createObjectURL(res);
            // // window.open(fileURL)
            // saveAs(fileURL);
            // callback(fileURL);
        })
        .catch(error => {
            if (isExpired) return;
            console.error('Show Error:', error);
            if (error?.status === 404) {
                return toast.error("File không tồn tại hoặc đã bị mất, vui lòng thử lại!");
            }
            return toast.error("Download không thành công, vui lòng thử lại!");
        })
};
